/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import React, { memo, useCallback, useState, useMemo, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { useMutation } from '@apollo/react-hooks';
import { css } from '@compiled/react';

import type { DocNode } from '@atlaskit/adf-schema';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { useAnalyticsEvents, AnalyticsListener } from '@atlaskit/analytics-next';
import { CommentEdited } from '@atlaskit/comment';
import type { ExtensionHandlers } from '@atlaskit/editor-common/extensions';
import { RendererActionsContext } from '@atlaskit/renderer/actions';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import { fg } from '@confluence/feature-gating';
import {
	ADFRenderer,
	useEventHandlers,
	createSmartCardEventHandler,
	createLinkEventHandler,
} from '@confluence/adf-renderer';
import { getExtensionHandlers, FALLBACK_REASONS } from '@confluence/comment-extension-handlers';
import { DynamicAnalyticsListener, editorChannel } from '@confluence/unique-page-load-id';
import { useRouteActions, useMatchRoute } from '@confluence/route-manager/entry-points/RouteState';
import { expVal } from '@confluence/feature-experiments';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode';
import { PageMode } from '@confluence/page-utils/entry-points/enums';
import { useSessionData } from '@confluence/session-data';
import { usePageContentId } from '@confluence/page-context';
import { CommentType } from '@confluence/comments-data';
import { TimeAgo } from '@confluence/time-ago';
import { getValidDate } from '@confluence/comments-util/entry-points/dateUtils';
import { AnalyticsSource } from '@confluence/comments-util/entry-points/analytics';

import { CommentRendererLocation } from './commentTypes';
import type {
	CommentRendererMarkInlineCommentsReadMutation as CommentRendererMarkInlineCommentsReadMutation$data,
	CommentRendererMarkInlineCommentsReadMutationVariables as CommentRendererMarkInlineCommentsReadMutation$variables,
} from './__types__/CommentRendererMarkInlineCommentsReadMutation';

const i18n = defineMessages({
	editedInParentheses: {
		id: 'comment.renderer.edited.in.parentheses',
		defaultMessage: '(edited)',
		description:
			'This text is shown as a label under the comment content to indicate that a comment has been edited',
	},
	editedBy: {
		id: 'comment.renderer.edited.by',
		defaultMessage: 'Edited by {username} {at}',
		description:
			'This text is shown in the edited label tooltip to display the user who edited the comment and at what time the comment was edited. The {at} will be replaced with a TimeAgo value with preposition enabled.',
	},
	anonymous: {
		id: 'comment.renderer.display.name.anonymous',
		defaultMessage: 'Anonymous',
		description: 'Text used in place of a missing username',
	},
});

const CommentRendererMarkInlineCommentsReadMutation = gql`
	mutation CommentRendererMarkInlineCommentsReadMutation($commentIds: [ID!]!) {
		markCommentsAsRead(input: { commentIds: $commentIds }) {
			success
			errors
		}
	}
`;
// Approx Scroll Height for Comment Container with 2 Lines
const MIN_SCROLL_HEIGHT = 50;

const standaloneStyle = css({
	paddingTop: token('space.100'),
	paddingBottom: token('space.100'),
});

const commentsPanelV1Style = css({
	paddingTop: token('space.150'),
	paddingBottom: token('space.150'),
});

const commentsPanelV2Style = css({
	paddingBottom: 'unset',
});

const commentEditedLabelStyle = css({
	color: token('color.text.subtlest'),
	font: token('font.body.UNSAFE_small'),
	paddingTop: token('space.075'),
});

export const handleIntersection = (
	entry: IntersectionObserverEntry,
	hasBeenViewed: boolean,
	commentId: string,
	setHasBeenViewed: (_viewed: boolean) => void,
	markCommentsRead: any,
) => {
	if (entry.isIntersecting) {
		// Comment entered the viewport
		setHasBeenViewed(true);
	} else if (hasBeenViewed) {
		// Comment left the viewport after it was viewed
		void markCommentsRead({
			variables: { commentIds: [commentId] },
		});
	}
};

export type CommentRendererProps = {
	commentId: string;
	adf?: DocNode;
	onContentFallback?: (extensionKey?: string) => void;
	isTruncatedContent?: boolean;
	onRendered?: () => void;
	maxHeight?: number;
	fadeOutHeight?: number;
	onComplete?: () => void;
	isInlineComment?: boolean;
	isCommentsPanel?: boolean;
	isEdited?: boolean;
	versionByDisplayName?: string | null;
	versionDate?: string | null;
	commentType?: CommentType;
};

const CommentRenderer = memo(
	({
		commentId,
		adf,
		onContentFallback,
		isTruncatedContent,
		onRendered,
		maxHeight,
		fadeOutHeight,
		onComplete,
		isInlineComment,
		isCommentsPanel = false,
		isEdited,
		versionByDisplayName,
		versionDate,
		commentType,
	}: CommentRendererProps) => {
		const { formatMessage } = useIntl();
		const pageMode = useGetPageMode();
		const [contentId] = usePageContentId();
		const [rootEl, setRootEl] = useState<HTMLDivElement | null>(null);
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const { cloudId, userId } = useSessionData();

		const [hasBeenViewed, setHasBeenViewed] = useState(false);
		const [markCommentsRead] = useMutation<
			CommentRendererMarkInlineCommentsReadMutation$data,
			CommentRendererMarkInlineCommentsReadMutation$variables
		>(CommentRendererMarkInlineCommentsReadMutation);
		useEffect(() => {
			if (!rootEl || !fg('mark_page_comments_as_read_upon_observation')) return;

			// eslint-disable-next-line no-restricted-syntax
			const observer = new IntersectionObserver(
				([entry]) =>
					handleIntersection(entry, hasBeenViewed, commentId, setHasBeenViewed, markCommentsRead),
				{ threshold: 0.1 },
			);
			observer.observe(rootEl);
			return () => {
				observer.disconnect();
			};
		}, [rootEl, hasBeenViewed, commentId, markCommentsRead]);

		const handleContentFallback = (extensionKey?: string) => {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'fellback',
					actionSubject: 'comment',
					actionSubjectId: commentId,
					source: 'comment',
					attributes: {
						editor: 'v2',
						commentType: 'page',
						reason: extensionKey
							? `${FALLBACK_REASONS.UNSUPPORTED_EXTENSION} - ${extensionKey}`
							: FALLBACK_REASONS.INVALID_ADF,
					},
				},
			}).fire();

			onContentFallback && onContentFallback(extensionKey);
		};

		const extensionHandlers: ExtensionHandlers = getExtensionHandlers(
			handleContentFallback,
			commentId,
		);
		const { push } = useRouteActions();
		const matchRoute = useMatchRoute();

		const embeddedConfluenceEventHandlers = useEventHandlers();

		const eventHandlers = useMemo(
			() => ({
				...createSmartCardEventHandler({ matchRoute, push }),
				...createLinkEventHandler({ matchRoute, push }),
				...embeddedConfluenceEventHandlers,
			}),
			[matchRoute, push, embeddedConfluenceEventHandlers],
		);

		/* Logic to not show Fade when we have 2 lines in Inactive state. When we have more than 2 lines the scrollHeight is
greater than 50, if its less than 50 send fadeOutHeight as 0*/
		const getFadeHeight = () => {
			if (
				isTruncatedContent &&
				(rootEl?.firstElementChild?.scrollHeight ?? 0) > MIN_SCROLL_HEIGHT
			) {
				return fadeOutHeight;
			} else {
				return 0;
			}
		};

		const injectObjectIdIntoRendererAnalytics = (objectId: string) => (event: UIAnalyticsEvent) => {
			// WS-3190 If a renderer event is fired, inject the commentId as objectId
			if (event.payload.actionSubject === 'renderer') {
				event.update((payload) => ({
					...payload,
					attributes: {
						...payload.attributes,
						appearance: 'comment',
						objectId,
					},
				}));
			}
		};

		const getRendererLocation = useCallback(() => {
			let rendererLocation = isInlineComment
				? CommentRendererLocation.STANDALONE_INLINE
				: CommentRendererLocation.STANDALONE_OBJECT;

			if (isCommentsPanel) {
				if (isInlineComment) {
					rendererLocation = CommentRendererLocation.COMMENTS_PANEL_INLINE;
				} else {
					rendererLocation = CommentRendererLocation.COMMENTS_PANEL_OBJECT;
				}
			}

			return rendererLocation;
		}, [isInlineComment, isCommentsPanel]);

		const handleEmojiLoadFail = useCallback(
			(emojiId?: string, reason?: string) => {
				const rendererLocation = getRendererLocation();

				createAnalyticsEvent({
					type: 'sendOperationalEvent',
					data: {
						source: pageMode === PageMode.VIEW ? 'viewPageScreen' : 'editPageScreen',
						objectType: 'page',
						objectId: contentId,
						action: 'failed',
						actionSubject: 'emojiLoad',
						actionSubjectId: 'commentRenderer',
						attributes: {
							commentId,
							location: rendererLocation,
							isLivePage: pageMode === PageMode.LIVE,
							emojiId,
							tenantId: cloudId,
							userId,
							reason,
							pageId: contentId,
						},
					},
				}).fire();
			},
			[pageMode, contentId, commentId, cloudId, userId, getRendererLocation, createAnalyticsEvent],
		);

		const handleEmojiLoadSuccess = useCallback(
			(emojiId?: string) => {
				const rendererLocation = getRendererLocation();

				createAnalyticsEvent({
					type: 'sendOperationalEvent',
					data: {
						source: pageMode === PageMode.VIEW ? 'viewPageScreen' : 'editPageScreen',
						objectType: 'page',
						objectId: contentId,
						action: 'success',
						actionSubject: 'emojiLoad',
						actionSubjectId: 'commentRenderer',
						attributes: {
							commentId,
							location: rendererLocation,
							isLivePage: pageMode === PageMode.LIVE,
							emojiId,
							tenantId: cloudId,
							userId,
							pageId: contentId,
						},
					},
				}).fire();
			},
			[pageMode, contentId, commentId, cloudId, userId, getRendererLocation, createAnalyticsEvent],
		);

		if (!adf) {
			return null;
		}

		let analyticsContextSource = AnalyticsSource.COMMENTS_PANEL;

		if (!isCommentsPanel) {
			if (commentType === CommentType.GENERAL) {
				analyticsContextSource = AnalyticsSource.PAGE_FOOTER;
			} else {
				analyticsContextSource = AnalyticsSource.STANDALONE;
			}
		}

		return (
			<AnalyticsListener
				onEvent={injectObjectIdIntoRendererAnalytics(commentId)}
				channel="editor" // We use the same channel for editor/renderer https://data-portal.internal.atlassian.com/analytics/registry/27447
			>
				<DynamicAnalyticsListener channelParam={editorChannel}>
					<div
						css={[
							!isCommentsPanel && standaloneStyle,
							isCommentsPanel &&
								!fg('confluence_frontend_comments_panel_v2') &&
								commentsPanelV1Style,
							isCommentsPanel &&
								fg('confluence_frontend_comments_panel_v2') &&
								commentsPanelV2Style,
						]}
						ref={setRootEl}
					>
						<RendererActionsContext>
							<>
								<ADFRenderer
									contentId={commentId}
									document={adf}
									extensionHandlers={extensionHandlers}
									portal={rootEl ?? undefined}
									allowStickyHeaders={false}
									isTruncatedContent={isTruncatedContent}
									onRendered={onRendered}
									onComplete={onComplete}
									allowSelectAllTrap
									maxHeight={maxHeight}
									fadeOutHeight={getFadeHeight()}
									appearance="comment"
									eventHandlers={eventHandlers}
									UNSTABLE_allowTableAlignment={
										!isInlineComment &&
										expVal<boolean>(
											'platform_editor_support_table_in_comment_exp',
											'isEnabled',
											false,
										)
									}
									UNSTABLE_allowTableResizing={
										!isInlineComment &&
										expVal<boolean>(
											'platform_editor_support_table_in_comment_exp',
											'isEnabled',
											false,
										)
									}
									shouldRemoveEmptySpaceAroundContent={fg(
										'confluence_frontend_remove_space_around_comment',
									)}
									onEmojiLoadFail={handleEmojiLoadFail}
									onEmojiLoadSuccess={handleEmojiLoadSuccess}
								/>
								{isEdited && fg('confluence_frontend_comment_edited_label_v2') && (
									<div css={[commentEditedLabelStyle]}>
										<Tooltip
											content={formatMessage(i18n.editedBy, {
												username: versionByDisplayName || formatMessage(i18n.anonymous),
												at: <TimeAgo date={getValidDate(versionDate)} usePrepositions />,
											})}
											analyticsContext={{
												attributes: {
													source: analyticsContextSource,
													commentType,
													name: 'commentEditedBadgeTooltip',
												},
											}}
										>
											{/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
											<span data-testid="comment-edited-label" tabIndex={0}>
												<CommentEdited>{formatMessage(i18n.editedInParentheses)}</CommentEdited>
											</span>
										</Tooltip>
									</div>
								)}
							</>
						</RendererActionsContext>
					</div>
				</DynamicAnalyticsListener>
			</AnalyticsListener>
		);
	},
);
CommentRenderer.displayName = 'CommentRenderer';
export { CommentRenderer };
